// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import getLocalizedTime from "@insite/client-framework/Common/Utilities/getLocalizedTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";

import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SectionTitle from "@pace/App/Components/Common/SectionTitle";
import { CHECKOUT_WHEN_TO_DELIVER } from "@pace/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@pace/Overrides/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import { sharedDateTimeOptions } from "@pace/XDL/constants";
import { parseBoolean } from "@pace/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
        // Xng-Change
        sessionFulfillmentMethod: state.context.session.fulfillmentMethod,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // XNG-Changes
    container?: InjectableCss;
    section?: InjectableCss;
    sectionTwoColums?: InjectableCss;
    sectionTitle?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    hr?: InjectableCss;
    orderDetails?: InjectableCss;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
    },
    orderNumberGridItem: {
        width: 12,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: 5px;
            padding-bottom: 15px;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
    // XNG-Changes
    fields,
    sessionFulfillmentMethod,
}: Props) => {
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }

        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber, isOrderConfirmationPage: true } as any);
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    /**
     * Xng-Change
     * for order approvals use session fulfillment method
     * for other orders use OOTB cart.fulfillmentMethod
     */
    const getFulfillmentMethod = () => cart.approverReason ? sessionFulfillmentMethod : cart.fulfillmentMethod;

    // XNG-Changes: structure and add additional fields
    // call getFulfillmentMethod instead of checking cart.fulfillmentMethod directly
    return (
        <>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.section} {...{ fullWidth: true }}>
                    <Typography {...styles.orderNumberHeading}>
                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                        {`${translate("Order")} `}
                        {/** XNG-Changes: PAC-750 */}
                        <Typography {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                            {cart.erpOrderNumber ?
                                `# ${cart.erpOrderNumber}`
                                :
                                cart.properties.preOrderNumber
                                ?
                                `# ${cart.properties.preOrderNumber}`
                                :
                                cart.orderNumber
                                ?
                                `# ${cart.orderNumber}`
                                :
                                siteMessage("Custom_Order_Confirmation_No_ERP_Number")}
                        </Typography>
                    </Typography>
                    <StyledWrapper {...styles.orderDetails}>
                        <StyledWrapper>
                            <Typography {...styles.sectionTitle}>{translate("Order Date")}</Typography>
                            <Typography {...styles.value}>
                                {getLocalizedDateTime({
                                    dateTime: new Date(cart.orderDate as Date),
                                    language,
                                    // XNG-Changes
                                    options:{
                                        ...sharedDateTimeOptions,
                                    }
                                })}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Typography {...styles.sectionTitle}>{translate("Status")}</Typography>
                            <Typography {...styles.value}>{order?.statusDisplay || cart.statusDisplay}</Typography>
                        </StyledWrapper>
                        {/** XNG-Changes: PAC-754 */}
                        {fields.showSalespersonName && cart?.salespersonName && (
                            <StyledWrapper>
                                <Typography {...styles.sectionTitle}>{translate("Salesperson")}</Typography>
                                <Typography {...styles.value}>{cart.salespersonName}</Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
            <SectionTitle
                title={
                    getFulfillmentMethod() === FulfillmentMethod.PickUp
                        ? translate("Will Call")
                        : translate("Addresses")
                }
                border="none"
            />
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.section}>
                    <OrderConfirmationShippingInformation
                        cart={cart}
                        shipTo={shipTo}
                        pickUpWarehouse={pickUpWarehouse}
                        extendedStyles={styles.orderConfirmationShippingInformation}
                    />
                </StyledWrapper>
                {getFulfillmentMethod() === FulfillmentMethod.Ship && (
                    <StyledWrapper {...styles.section}>
                        <OrderConfirmationBillingInformation
                            cart={cart}
                            billTo={billTo}
                            extendedStyles={styles.orderConfirmationBillingInformation}
                        />
                    </StyledWrapper>
                )}

                {getFulfillmentMethod() === FulfillmentMethod.PickUp && cart.requestedPickupDate && (
                    <StyledWrapper {...styles.section}>
                        <Typography {...styles.sectionTitle}>{translate("Requested Pickup Date")}</Typography>
                        <Typography {...styles.value} data-test-selector="requestedPickupDateValue">
                            <Typography {...styles.label}>{translate("Date")}:</Typography>
                            <LocalizedDateTime
                                dateTime={new Date(cart.requestedPickupDate)}
                                // XNG-Changes
                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                            />
                        </Typography>
                        {/** XNG-Changes */}
                        <Typography {...styles.value} data-test-selector="requestedDeliveryDateTimeValue">
                            <Typography {...styles.label}>{translate("Time")}:</Typography>
                            {parseBoolean(cart.properties.deliverAnytime)
                                ? translate("Anytime")
                                : getLocalizedTime({
                                    dateTime: new Date(cart.requestedPickupDate),
                                    options: {
                                        hour: "numeric",
                                        minute: "numeric",
                                    },
                                })}
                        </Typography>
                    </StyledWrapper>
                )}

                {cart.notes && (
                    <StyledWrapper {...styles.section} {...{ fullWidth: true }}>
                        <Typography {...styles.sectionTitle}>
                            {translate("Special Shipping / Handling Instructions")}
                        </Typography>
                        <Typography {...styles.value}>{cart.notes}</Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {getFulfillmentMethod() === FulfillmentMethod.Ship && (
                <>
                    <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
                    <SectionTitle title={translate("Shipping")} border="none" />
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.section}>
                            <Typography {...styles.sectionTitle}>{translate("Preference")}</Typography>
                            <Typography {...styles.value}>
                                {parseBoolean(cart.properties.shipPartial)
                                    ? CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAvailable
                                    : CHECKOUT_WHEN_TO_DELIVER.DeliverWhenAllAvailable}
                            </Typography>
                        </StyledWrapper>
                        {getFulfillmentMethod() === FulfillmentMethod.Ship && (
                            <>
                                {cart.requestedDeliveryDate && (
                                    <StyledWrapper {...styles.section}>
                                        <Typography {...styles.sectionTitle}>
                                            {translate("Requested Delivery Date")}
                                        </Typography>
                                        <Typography {...styles.value} data-test-selector="requestedDeliveryDateValue">
                                            <Typography {...styles.label}>{translate("Date")}:</Typography>
                                            <LocalizedDateTime
                                                dateTime={new Date(cart.requestedDeliveryDate)}
                                                // XNG-Changes
                                                options={{ year: "numeric", month: "2-digit", day: "2-digit" }}
                                            />
                                        </Typography>
                                        {/** XNG-Changes */}
                                        <Typography
                                            {...styles.value}
                                            data-test-selector="requestedDeliveryDateTimeValue"
                                        >
                                            <Typography {...styles.label}>{translate("Time")}:</Typography>
                                            {parseBoolean(cart.properties.deliverAnytime)
                                                ? translate("Anytime")
                                                : getLocalizedTime({
                                                    dateTime: new Date(cart.requestedDeliveryDate),
                                                    options: {
                                                        hour: "numeric",
                                                        minute: "numeric",
                                                    },
                                                })}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                                {/** XNG-Changes: PAC-530 */}
                                {/* {cart.carrier?.description && (
                                    <StyledWrapper {...styles.section}>
                                        <Typography {...styles.sectionTitle}>{translate("Carrier")}</Typography>
                                        <Typography {...styles.value}>{cart.carrier!.description}</Typography>
                                    </StyledWrapper>
                                )}
                                {cart.shipVia?.description && (
                                    <StyledWrapper {...styles.section}>
                                        <Typography {...styles.sectionTitle}>{translate("Service")}</Typography>
                                        <Typography {...styles.value}>{cart.shipVia?.description}</Typography>
                                    </StyledWrapper>
                                )} */}
                            </>
                        )}
                    </StyledWrapper>
                </>
            )}
            {(cart.poNumber ||
                cart.properties.jobNumber ||
                !!cart.properties.contactName ||
                !!cart.properties.contactPhone ||
                (enableVat && cart.customerVatNumber)) && (
                    <>
                        <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
                        <StyledWrapper {...styles.container}>
                            {(cart.poNumber || cart.properties.jobNumber) && (
                                <StyledWrapper {...styles.section}>
                                    <StyledWrapper {...styles.sectionTwoColums}>
                                        {cart.poNumber && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Purchase Order #")}
                                                </Typography>
                                                <Typography {...styles.value}>{cart.poNumber}</Typography>
                                            </StyledWrapper>
                                        )}
                                        {cart.properties.jobNumber && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Job Name / #")}
                                                </Typography>
                                                <Typography {...styles.value}>{cart.properties.jobNumber}</Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {/** XNG-Changes: PAC-753 */}
                            {(!!cart.properties.contactName || !!cart.properties.contactPhone) && (
                                <StyledWrapper {...styles.section}>
                                    <StyledWrapper {...styles.sectionTwoColums}>
                                        {cart.properties.contactName && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Contact Name")}
                                                </Typography>
                                                <Typography {...styles.value}>{cart.properties.contactName}</Typography>
                                            </StyledWrapper>
                                        )}
                                        {cart.properties.contactPhone && (
                                            <StyledWrapper>
                                                <Typography {...styles.sectionTitle}>
                                                    {translate("Contact Phone #")}
                                                </Typography>
                                                <Typography {...styles.value}>{cart.properties.contactPhone}</Typography>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {enableVat && cart.customerVatNumber && (
                                <StyledWrapper {...styles.section}>
                                    <StyledWrapper {...styles.sectionTwoColums}>
                                        <StyledWrapper>
                                            <Typography {...styles.sectionTitle}>{translate("VAT Number")}</Typography>
                                            <Typography {...styles.value}>{cart.customerVatNumber}</Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </>
                )}
            {!cart.requiresApproval && (
                <>
                    <StyledWrapper {...styles.hr} {...{ addMargin: true }} />
                    <StyledWrapper {...styles.container}>
                        <StyledWrapper {...styles.section}>
                            <SectionTitle title={translate("Payment")} border="none" />
                            <Typography {...styles.sectionTitle}>
                                {cart.paymentMethod?.isCreditCard || cart.paymentMethod?.isPaymentProfile
                                    ? translate("Credit Card")
                                    : cart.paymentMethod?.description}
                            </Typography>
                            {(cart.paymentMethod?.isCreditCard || cart.paymentMethod?.isPaymentProfile) && (
                                <Typography
                                    {...styles.value}
                                >{`${cart.properties.cardType} **** ${cart.properties.lastFourDigits}   ${cart.properties.expirationDate}`}</Typography>
                            )}
                        </StyledWrapper>
                        {getFulfillmentMethod() === FulfillmentMethod.PickUp && (
                            <StyledWrapper {...styles.section}>
                                <SectionTitle title={translate("Addresses")} border="none" />
                                <OrderConfirmationBillingInformation
                                    cart={cart}
                                    billTo={billTo}
                                    extendedStyles={styles.orderConfirmationBillingInformation}
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
        // XNG-Changes: PAC-754
        fieldDefinitions: [
            {
                name: "showSalespersonName",
                displayName: "Show Salesperson Name",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
